import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../utils/apiClient';

const BASE = process.env.REACT_APP_API_BASE_URL;

const ROLES = ['etudiant', 'tuteur', 'supertuteur', 'professeur', 'parent', 'admin'];
const ROLE_STYLE = {
    admin:       'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    supertuteur: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
    tuteur:      'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    professeur:  'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
    etudiant:    'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};


function TuteurManager({ studentId, canAssign }) {
    const [tuteurs, setTuteurs]       = useState([]);
    const [subjects, setSubjects]     = useState([]);
    const [tutors, setTutors]         = useState([]);
    const [loading, setLoading]       = useState(true);
    const [adding, setAdding]         = useState(false);
    const [form, setForm]             = useState({ subject_id: '', tuteur_id: '' });
    const [busy, setBusy]             = useState(false);

    const loadTuteurs = useCallback(async () => {
        const res  = await apiClient(`${BASE}/admin_students.php?action=list_student_tuteurs&student_id=${studentId}`);
        const json = await res.json();
        if (json.success) setTuteurs(json.tuteurs);
        setLoading(false);
    }, [studentId]);

    useEffect(() => {
        loadTuteurs();
        if (canAssign) {
            apiClient(`${BASE}/admin_students.php?action=list_student_subjects&student_id=${studentId}`)
                .then(r => r.json()).then(j => { if (j.success) setSubjects(j.subjects); });
            apiClient(`${BASE}/admin_students.php?action=list_tutors`)
                .then(r => r.json()).then(j => { if (j.success) setTutors(j.tutors); });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadTuteurs, canAssign]);

    const assign = async () => {
        if (!form.subject_id || !form.tuteur_id) return;
        setBusy(true);
        await apiClient(`${BASE}/admin_students.php?action=assign_tuteur_subject`, {
            method: 'POST',
            body: JSON.stringify({ student_id: studentId, subject_id: parseInt(form.subject_id), tuteur_id: parseInt(form.tuteur_id) }),
        });
        await loadTuteurs();
        setForm({ subject_id: '', tuteur_id: '' });
        setAdding(false);
        setBusy(false);
    };

    const remove = async (subjectId) => {
        setBusy(true);
        await apiClient(`${BASE}/admin_students.php?action=remove_tuteur_subject`, {
            method: 'POST',
            body: JSON.stringify({ student_id: studentId, subject_id: subjectId }),
        });
        await loadTuteurs();
        setBusy(false);
    };

    const assignedSubjectIds = new Set(tuteurs.map(t => String(t.subject_id)));
    const availableSubjects  = subjects.filter(s => !assignedSubjectIds.has(String(s.id)));

    if (loading) return <p className="text-sm text-gray-400">Chargement…</p>;

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tuteurs par matière</p>
                {canAssign && availableSubjects.length > 0 && !adding && (
                    <button onClick={() => setAdding(true)}
                        className="text-xs px-2 py-0.5 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 border border-cyan-200 dark:border-cyan-500/30 hover:bg-cyan-100 transition-colors">
                        + Assigner
                    </button>
                )}
            </div>

            {tuteurs.length === 0 && !adding && (
                <p className="text-xs text-gray-400 italic">Aucun tuteur assigné.</p>
            )}

            <div className="flex flex-col gap-1.5">
                {tuteurs.map(t => (
                    <div key={t.subject_id} className="flex items-center justify-between px-3 py-2 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-500/20">
                        <div>
                            <p className="text-xs font-semibold text-blue-800 dark:text-blue-200">{t.subject_name}</p>
                            <p className="text-xs text-blue-600 dark:text-blue-400">{t.tuteur_name}</p>
                        </div>
                        {canAssign && (
                            <button disabled={busy} onClick={() => remove(t.subject_id)}
                                className="text-xs text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors disabled:opacity-50 shrink-0 ml-2">
                                Retirer
                            </button>
                        )}
                    </div>
                ))}
            </div>

            {adding && canAssign && (
                <div className="mt-2 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-white/10 space-y-2">
                    <select value={form.subject_id} onChange={e => setForm(f => ({ ...f, subject_id: e.target.value }))}
                        className="w-full text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-cyan-500">
                        <option value="">— Matière —</option>
                        {availableSubjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <select value={form.tuteur_id} onChange={e => setForm(f => ({ ...f, tuteur_id: e.target.value }))}
                        className="w-full text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-cyan-500">
                        <option value="">— Tuteur —</option>
                        {tutors.map(t => <option key={t.id} value={t.id}>{t.name} ({t.role})</option>)}
                    </select>
                    <div className="flex gap-2">
                        <button onClick={() => { setAdding(false); setForm({ subject_id: '', tuteur_id: '' }); }}
                            className="flex-1 text-xs py-1 rounded-lg border border-gray-200 dark:border-white/10 text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
                            Annuler
                        </button>
                        <button onClick={assign} disabled={busy || !form.subject_id || !form.tuteur_id}
                            className="flex-1 text-xs py-1 rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white font-medium transition-colors">
                            {busy ? '…' : 'Assigner'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function StudentDetailPanel({ student, onClose, onMessage, canAssign }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview'); // overview, notions, skills, placement, config
    const [expandedChapters, setExpandedChapters] = useState({});
    const [skillSearch, setSkillSearch] = useState('');
    const [skillFilter, setSkillFilter] = useState('all'); // all, attempted, not_attempted

    useEffect(() => {
        setLoading(true);
        apiClient(`${BASE}/admin_students.php?action=student_full_detail&student_id=${student.id}`)
            .then(r => r.json())
            .then(j => { if (j.success) setDetail(j.student); })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    }, [student.id]);

    const initial = (student.name || '?')[0].toUpperCase();

    const toggleChapter = (chapterName) => {
        setExpandedChapters(prev => ({ ...prev, [chapterName]: !prev[chapterName] }));
    };

    // Helper: Notion Status Style
    const getStatusStyle = (status) => {
        switch (status) {
            case 'mastered':
                return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/30';
            case 'advanced':
                return 'bg-teal-50 text-teal-700 dark:bg-teal-950/20 dark:text-teal-400 border-teal-200 dark:border-teal-800/30';
            case 'practice':
                return 'bg-blue-50 text-blue-700 dark:bg-blue-950/20 dark:text-blue-400 border-blue-200 dark:border-blue-800/30';
            case 'unlocked':
                return 'bg-purple-50 text-purple-700 dark:bg-purple-950/20 dark:text-purple-400 border-purple-200 dark:border-purple-800/30';
            case 'locked':
            default:
                return 'bg-gray-50 text-gray-500 dark:bg-gray-800/30 dark:text-gray-400 border-gray-200 dark:border-gray-750';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'mastered': return 'Maîtrisé';
            case 'advanced': return 'Avancé';
            case 'practice': return 'Entraînement';
            case 'unlocked': return 'Débloqué';
            case 'locked':
            default: return 'Verrouillé';
        }
    };

    if (loading) {
        return (
            <div className="fixed inset-0 z-[110] flex justify-end" onClick={onClose}>
                <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                <div className="relative z-10 w-full max-w-4xl bg-white dark:bg-gray-900 h-full shadow-2xl flex flex-col items-center justify-center p-8">
                    <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-gray-500 dark:text-gray-400 font-medium">Chargement du profil élève...</p>
                </div>
            </div>
        );
    }

    if (!detail) return null;

    // Group notions by chapter for progression tab
    const notionsByChapter = {};
    if (detail.notions) {
        detail.notions.forEach(n => {
            if (!notionsByChapter[n.chapter_name]) {
                notionsByChapter[n.chapter_name] = [];
            }
            notionsByChapter[n.chapter_name].push(n);
        });
    }

    // Compute stats
    const totalNotions = detail.notions ? detail.notions.length : 0;
    const masteredNotionsCount = detail.notions ? detail.notions.filter(n => ['mastered', 'advanced'].includes(n.status)).length : 0;
    const progressionPercent = totalNotions > 0 ? Math.round((masteredNotionsCount / totalNotions) * 100) : 0;

    // Filter exercises
    const filteredSkills = detail.skills ? detail.skills.filter(s => {
        const matchesSearch = s.title.toLowerCase().includes(skillSearch.toLowerCase()) || 
                             (s.subject_name && s.subject_name.toLowerCase().includes(skillSearch.toLowerCase()));
        if (skillFilter === 'attempted') {
            return matchesSearch && s.attempt_count > 0;
        } else if (skillFilter === 'not_attempted') {
            return matchesSearch && (!s.attempt_count || s.attempt_count === 0);
        }
        return matchesSearch;
    }) : [];

    return (
        <div className="fixed inset-0 z-[110] flex justify-end" onClick={onClose}>
            <div className="absolute inset-0 bg-black/45 backdrop-blur-sm transition-opacity duration-300" />
            <div
                className="relative z-10 w-full max-w-5xl bg-gray-50 dark:bg-gray-950 h-full shadow-2xl flex flex-col overflow-hidden transition-transform duration-300 transform translate-x-0"
                onClick={e => e.stopPropagation()}>

                {/* HEADER */}
                <div className="flex flex-col md:flex-row md:items-center justify-between p-6 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-white/10 gap-4 shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-cyan-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-cyan-500/20 shrink-0">
                            {initial}
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{detail.name}</h3>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800/30">
                                    Élève
                                </span>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{detail.email}</p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            onClick={() => onMessage(detail.id)}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold transition-all hover:shadow-lg hover:shadow-cyan-600/10">
                            ✉ Message
                        </button>
                        <button onClick={onClose}
                            className="p-2 px-3.5 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm font-medium">
                            ✕ Fermer
                        </button>
                    </div>
                </div>

                {/* TABS SELECTOR */}
                <div className="flex border-b border-gray-200 dark:border-white/10 bg-white dark:bg-gray-900 px-6 overflow-x-auto gap-6 shrink-0">
                    {[
                        { id: 'overview', label: 'Vue d\'ensemble' },
                        { id: 'notions', label: 'Notions & Chapitres' },
                        { id: 'skills', label: 'Exercices' },
                        { id: 'placement', label: 'Tests de Positionnement' },
                        { id: 'history', label: 'Historique SRS & Activités' },
                        { id: 'config', label: 'Tuteurs & Classes' },
                    ].map(t => (
                        <button
                            key={t.id}
                            onClick={() => setActiveTab(t.id)}
                            className={`py-3.5 text-sm font-semibold border-b-2 transition-all relative shrink-0 ${
                                activeTab === t.id
                                    ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400 font-bold'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                            }`}>
                            {t.label}
                            {activeTab === t.id && (
                                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-500 rounded-full" />
                            )}
                        </button>
                    ))}
                </div>

                {/* CONTENT AREA */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">

                    {/* TAB: OVERVIEW */}
                    {activeTab === 'overview' && (
                        <div className="space-y-6">
                            {/* Stat Badges Grid */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                <div className="bg-white dark:bg-gray-900 p-5 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm flex flex-col">
                                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Progression notions</span>
                                    <span className="text-3xl font-extrabold text-gray-800 dark:text-white mt-2">{progressionPercent}%</span>
                                    <span className="text-xs text-gray-500 mt-1">{masteredNotionsCount} / {totalNotions} maîtrisées</span>
                                </div>
                                <div className="bg-white dark:bg-gray-900 p-5 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm flex flex-col">
                                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Exercices résolus</span>
                                    <span className="text-3xl font-extrabold text-gray-800 dark:text-white mt-2">{detail.skills_done}</span>
                                    <span className="text-xs text-gray-500 mt-1">Exercices validés</span>
                                </div>
                                <div className="bg-white dark:bg-gray-900 p-5 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm flex flex-col">
                                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Flashcards</span>
                                    <span className="text-3xl font-extrabold text-gray-800 dark:text-white mt-2">{detail.flashcards_count}</span>
                                    <span className="text-xs text-gray-500 mt-1">Réviseur actif</span>
                                </div>
                                <div className="bg-white dark:bg-gray-900 p-5 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm flex flex-col">
                                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Quizzes complétés</span>
                                    <span className="text-3xl font-extrabold text-gray-800 dark:text-white mt-2">{detail.quizzes_count}</span>
                                    <span className="text-xs text-gray-500 mt-1">Évaluations continues</span>
                                </div>
                            </div>

                            {/* Two-Column Overview */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Left: Classes & Tuteurs */}
                                <div className="space-y-6">
                                    <div className="bg-white dark:bg-gray-900 p-5 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm space-y-4">
                                        <h4 className="font-bold text-gray-800 dark:text-white text-sm uppercase tracking-wider">Classes d'affectation</h4>
                                        {detail.classes && detail.classes.length > 0 ? (
                                            <div className="flex flex-col gap-2">
                                                {detail.classes.map((c, i) => (
                                                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-teal-50 dark:bg-teal-900/10 border border-teal-100 dark:border-teal-800/20">
                                                        <div className="flex items-center gap-2.5">
                                                            <div className="w-2.5 h-2.5 rounded-full bg-teal-500 shrink-0" />
                                                            <span className="text-sm font-semibold text-teal-900 dark:text-teal-300">{c.classe_nom}</span>
                                                        </div>
                                                        <span className="text-xs text-teal-600 dark:text-teal-400">Prof: {c.professeur_name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-gray-400 italic">Aucune classe assignée pour le moment.</p>
                                        )}
                                    </div>

                                    <div className="bg-white dark:bg-gray-900 p-5 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm space-y-4">
                                        <h4 className="font-bold text-gray-800 dark:text-white text-sm uppercase tracking-wider">Tuteurs par matière</h4>
                                        {detail.tuteurs && detail.tuteurs.length > 0 ? (
                                            <div className="flex flex-col gap-2">
                                                {detail.tuteurs.map((t, i) => (
                                                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-800/20">
                                                        <div>
                                                            <span className="text-sm font-semibold text-indigo-950 dark:text-indigo-300">{t.subject_name}</span>
                                                            <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-0.5">Tuteur: {t.tuteur_name}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-gray-400 italic">Aucun tuteur affecté.</p>
                                        )}
                                    </div>
                                </div>

                                {/* Right: Placement & Recent Activity */}
                                <div className="space-y-6">
                                    <div className="bg-white dark:bg-gray-900 p-5 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm space-y-4">
                                        <h4 className="font-bold text-gray-800 dark:text-white text-sm uppercase tracking-wider">Derniers Tests de Positionnement</h4>
                                        {detail.placement_tests && detail.placement_tests.length > 0 ? (
                                            <div className="flex flex-col gap-2.5">
                                                {detail.placement_tests.slice(0, 3).map((p, i) => (
                                                    <div key={i} className="flex items-center justify-between p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
                                                        <div>
                                                            <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{p.chapter_name}</span>
                                                            <p className="text-sm font-bold text-gray-800 dark:text-white">Niveau {p.level_number}</p>
                                                        </div>
                                                        <div className="text-right">
                                                            <span className={`inline-block px-2.5 py-1 rounded-lg text-xs font-bold ${
                                                                p.passed 
                                                                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/20' 
                                                                    : 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300 border border-rose-200 dark:border-rose-500/20'
                                                            }`}>
                                                                {p.score} / {p.total_questions} ({p.passed ? 'Validé' : 'Échec'})
                                                            </span>
                                                            <p className="text-[10px] text-gray-400 mt-1">{new Date(p.attempted_at).toLocaleDateString('fr-FR')}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-gray-400 italic">Aucun test de positionnement effectué.</p>
                                        )}
                                    </div>

                                    <div className="bg-white dark:bg-gray-900 p-5 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm space-y-4">
                                        <h4 className="font-bold text-gray-800 dark:text-white text-sm uppercase tracking-wider">Informations d'inscription</h4>
                                        <div className="text-xs text-gray-600 dark:text-gray-400 space-y-2">
                                            <p><strong>Date d'inscription :</strong> {new Date(detail.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB: NOTIONS */}
                    {activeTab === 'notions' && (
                        <div className="space-y-6">
                            <h4 className="text-lg font-bold text-gray-800 dark:text-white">Avancement du programme par chapitre</h4>
                            <div className="flex flex-col gap-4">
                                {Object.entries(notionsByChapter).map(([chapterName, notions]) => {
                                    const total = notions.length;
                                    const mastered = notions.filter(n => ['mastered', 'advanced'].includes(n.status)).length;
                                    const percent = total > 0 ? Math.round((mastered / total) * 100) : 0;
                                    const isExpanded = expandedChapters[chapterName] || false;

                                    return (
                                        <div key={chapterName} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm overflow-hidden">
                                            <button
                                                onClick={() => toggleChapter(chapterName)}
                                                className="w-full flex flex-col md:flex-row md:items-center justify-between p-5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors gap-4 text-left">
                                                
                                                <div className="space-y-1">
                                                    <span className="text-xs font-semibold text-cyan-600 dark:text-cyan-400 uppercase tracking-wider">{notions[0].subject_name}</span>
                                                    <h5 className="font-bold text-gray-900 dark:text-white text-base">{chapterName}</h5>
                                                </div>

                                                <div className="flex items-center gap-6 w-full md:w-auto shrink-0 justify-between md:justify-end">
                                                    <div className="flex flex-col items-end gap-1">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-32 bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
                                                                <div className="bg-cyan-500 h-full rounded-full transition-all duration-500" style={{ width: `${percent}%` }} />
                                                            </div>
                                                            <span className="text-sm font-bold text-gray-700 dark:text-gray-300">{percent}%</span>
                                                        </div>
                                                        <span className="text-xs text-gray-500">{mastered} / {total} notions maîtrisées</span>
                                                    </div>
                                                    <span className="text-gray-400">{isExpanded ? '▲' : '▼'}</span>
                                                </div>
                                            </button>

                                            {isExpanded && (
                                                <div className="border-t border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-gray-950/20 p-5 space-y-3">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        {notions.map(n => (
                                                            <div key={n.notion_id} className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-white/10 shadow-sm flex flex-col justify-between gap-3">
                                                                <div className="flex items-start justify-between gap-3">
                                                                    <div className="space-y-0.5">
                                                                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Notion #{n.notion_number}</span>
                                                                        <p className="text-sm font-bold text-gray-800 dark:text-white">{n.notion_name}</p>
                                                                    </div>
                                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${getStatusStyle(n.status)}`}>
                                                                        {getStatusLabel(n.status)}
                                                                    </span>
                                                                </div>

                                                                {n.status !== 'locked' && (
                                                                    <div className="grid grid-cols-2 gap-2 text-xs border-t border-gray-100 dark:border-white/5 pt-2 text-gray-500">
                                                                        <div>
                                                                            <span className="text-[10px] text-gray-400 block uppercase">Int. Flashcards</span>
                                                                            <span className="font-semibold text-gray-700 dark:text-gray-300">
                                                                                {n.flashcard_avg_interval ? `${Math.round(n.flashcard_avg_interval)}j` : '—'}
                                                                            </span>
                                                                        </div>
                                                                        <div>
                                                                            <span className="text-[10px] text-gray-400 block uppercase">Score Quiz</span>
                                                                            <span className="font-semibold text-gray-700 dark:text-gray-300">
                                                                                {n.best_practice_score !== null ? `${n.best_practice_score}%` : '—'}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* TAB: SKILLS */}
                    {activeTab === 'skills' && (
                        <div className="space-y-6">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <h4 className="text-lg font-bold text-gray-800 dark:text-white">Exercices et compétences</h4>
                                
                                <div className="flex flex-wrap items-center gap-3">
                                    <input 
                                        type="text" 
                                        placeholder="Rechercher un exercice..." 
                                        value={skillSearch}
                                        onChange={e => setSkillSearch(e.target.value)}
                                        className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-800 dark:text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                    />
                                    <select 
                                        value={skillFilter} 
                                        onChange={e => setSkillFilter(e.target.value)}
                                        className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-800 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500">
                                        <option value="all">Tous</option>
                                        <option value="attempted">Tentés</option>
                                        <option value="not_attempted">Non tentés</option>
                                    </select>
                                </div>
                            </div>

                            {filteredSkills.length === 0 ? (
                                <p className="text-sm text-gray-400 italic text-center py-10 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm">Aucun exercice trouvé.</p>
                            ) : (
                                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm overflow-hidden overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 text-xs uppercase">
                                            <tr>
                                                <th className="p-4 text-left">Matière / Exercice</th>
                                                <th className="p-4 text-center">Tentatives</th>
                                                <th className="p-4 text-center">Meilleur Score</th>
                                                <th className="p-4 text-left">Dernier essai</th>
                                                <th className="p-4 text-left">Prochaine révision</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                                            {filteredSkills.map(s => {
                                                const hasScore = s.best_score !== null;
                                                const scoreColor = hasScore 
                                                    ? s.best_score >= 80 
                                                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/30' 
                                                        : s.best_score >= 50 
                                                            ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400 border border-amber-200 dark:border-amber-800/30'
                                                            : 'bg-rose-50 text-rose-700 dark:bg-rose-950/20 dark:text-rose-400 border border-rose-200 dark:border-rose-800/30'
                                                    : 'bg-gray-50 text-gray-400 dark:bg-gray-800 dark:text-gray-500 border border-gray-200 dark:border-gray-700';

                                                return (
                                                    <tr key={s.id} className="hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors">
                                                        <td className="p-4">
                                                            <span className="text-[10px] font-semibold text-cyan-600 dark:text-cyan-400 uppercase tracking-wider block">{s.subject_name || 'Général'}</span>
                                                            <span className="font-bold text-gray-850 dark:text-gray-200">{s.title}</span>
                                                        </td>
                                                        <td className="p-4 text-center font-semibold text-gray-600 dark:text-gray-400">
                                                            {s.attempt_count || 0}
                                                        </td>
                                                        <td className="p-4 text-center">
                                                            <span className={`inline-block px-2.5 py-0.5 rounded-lg text-xs font-bold ${scoreColor}`}>
                                                                {hasScore ? `${s.best_score}%` : '—'}
                                                            </span>
                                                        </td>
                                                        <td className="p-4 text-gray-500 text-xs">
                                                            {s.last_attempt_at 
                                                                ? new Date(s.last_attempt_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) 
                                                                : 'Jamais'}
                                                        </td>
                                                        <td className="p-4 text-gray-500 text-xs">
                                                            {s.next_review_date 
                                                                ? new Date(s.next_review_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) 
                                                                : 'Non programmée'}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB: PLACEMENT */}
                    {activeTab === 'placement' && (
                        <div className="space-y-6">
                            <h4 className="text-lg font-bold text-gray-800 dark:text-white">Historique des tests de positionnement</h4>
                            
                            {detail.placement_tests && detail.placement_tests.length > 0 ? (
                                <div className="relative border-l border-gray-200 dark:border-gray-800 ml-4 pl-6 space-y-6">
                                    {detail.placement_tests.map((p, i) => (
                                        <div key={i} className="relative">
                                            {/* Dot on the line */}
                                            <span className={`absolute -left-[31px] top-1.5 flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-gray-50 dark:ring-gray-950 ${
                                                p.passed 
                                                    ? 'bg-emerald-500' 
                                                    : 'bg-rose-500'
                                            }`} />
                                            
                                            <div className="bg-white dark:bg-gray-900 p-5 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                                <div>
                                                    <span className="text-xs font-bold text-cyan-600 dark:text-cyan-400 uppercase tracking-wider block">{p.chapter_name}</span>
                                                    <h5 className="font-extrabold text-gray-900 dark:text-white text-base">Niveau de Chapitre : {p.level_number}</h5>
                                                    <p className="text-xs text-gray-400 mt-1">Tentative le {new Date(p.attempted_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                                                </div>

                                                <div className="flex items-center gap-3 shrink-0">
                                                    <span className={`px-3 py-1 rounded-xl text-sm font-black border ${
                                                        p.passed 
                                                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/30' 
                                                            : 'bg-rose-50 text-rose-700 dark:bg-rose-950/20 dark:text-rose-400 border-rose-200 dark:border-rose-800/30'
                                                    }`}>
                                                        Score: {p.score} / {p.total_questions}
                                                    </span>
                                                    <span className={`text-xs font-bold uppercase tracking-wider ${p.passed ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                                        {p.passed ? '✓ Validé' : '✗ Échoué'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400 italic text-center py-10 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm">
                                    Aucun test de positionnement effectué par l'élève.
                                </p>
                            )}
                        </div>
                    )}

                    {/* TAB: SRS HISTORY */}
                    {activeTab === 'history' && (
                        <div className="space-y-6">
                            <h4 className="text-lg font-bold text-gray-800 dark:text-white">Historique SRS et Activités d'apprentissage</h4>
                            <p className="text-xs text-gray-500">
                                Retrouvez la trace de toutes les révisions de flashcards et de quizzes réalisées par l'élève, ainsi que leurs réponses détaillées.
                            </p>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Quiz History Column */}
                                <div className="space-y-4">
                                    <h5 className="text-sm font-bold text-cyan-600 dark:text-cyan-400 uppercase tracking-wider flex items-center gap-2">
                                        <span>🎯</span> Quizzes Complétés ({detail.quiz_history ? detail.quiz_history.length : 0})
                                    </h5>
                                    {(!detail.quiz_history || detail.quiz_history.length === 0) ? (
                                        <p className="text-sm text-gray-400 italic py-10 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm text-center">Aucun quiz complété.</p>
                                    ) : (
                                        <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                                            {detail.quiz_history.map((q, idx) => {
                                                const ratingStyle = q.rating === 2 
                                                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/30' 
                                                    : q.rating === 1 
                                                        ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400 border border-amber-200 dark:border-amber-800/30' 
                                                        : 'bg-rose-50 text-rose-700 dark:bg-rose-950/20 dark:text-rose-400 border border-rose-200 dark:border-rose-800/30';
                                                
                                                const ratingLabel = q.rating === 2 ? 'Facile' : q.rating === 1 ? 'Moyen' : 'Difficile';

                                                // Parse response data safely
                                                const studentAns = q.response_data && q.response_data.answer ? q.response_data.answer : null;

                                                return (
                                                    <div key={q.id || idx} className="bg-white dark:bg-gray-900 p-5 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm flex flex-col gap-3">
                                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-100 dark:border-white/5 pb-2">
                                                            <div>
                                                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{q.chapter_name} &gt; {q.notion_name}</span>
                                                                <span className="text-xs text-gray-500">{new Date(q.review_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                                            </div>
                                                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold self-start sm:self-auto ${ratingStyle}`}>
                                                                SRS : {ratingLabel}
                                                            </span>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <div className="text-sm">
                                                                <span className="text-xs font-bold text-gray-400 block uppercase mb-1">Question</span>
                                                                <p className="font-semibold text-gray-800 dark:text-gray-200">{q.quiz_question}</p>
                                                            </div>

                                                            {studentAns && (
                                                                <div className="text-xs bg-gray-50 dark:bg-gray-950/50 p-3 rounded-xl border border-gray-150 dark:border-gray-800 space-y-2">
                                                                    <span className="text-[10px] font-bold text-gray-400 uppercase block">Réponse fournie par l'élève</span>
                                                                    {typeof studentAns === 'object' ? (
                                                                        Array.isArray(studentAns) ? (
                                                                            <div className="flex flex-wrap gap-1">
                                                                                {studentAns.map((val, i) => (
                                                                                    <span key={i} className="px-1.5 py-0.5 rounded bg-gray-250 dark:bg-gray-800 text-gray-800 dark:text-gray-300 font-mono text-xs">{val}</span>
                                                                                ))}
                                                                            </div>
                                                                        ) : (
                                                                            <div className="grid grid-cols-1 gap-1">
                                                                                {Object.entries(studentAns).map(([k, v], i) => (
                                                                                    <div key={i} className="flex items-center gap-2">
                                                                                        <span className="px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-800 font-mono text-xs">{k}</span>
                                                                                        <span className="text-gray-400">➔</span>
                                                                                        <span className="px-1 py-0.5 rounded bg-cyan-100 dark:bg-cyan-950/40 text-cyan-800 dark:text-cyan-400 font-mono text-xs">{v}</span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )
                                                                    ) : (
                                                                        <span className="font-mono text-cyan-600 dark:text-cyan-400 font-bold">{studentAns.toString()}</span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Flashcard History Column */}
                                <div className="space-y-4">
                                    <h5 className="text-sm font-bold text-purple-600 dark:text-purple-400 uppercase tracking-wider flex items-center gap-2">
                                        <span>⚡</span> Flashcards Révisées ({detail.flashcard_history ? detail.flashcard_history.length : 0})
                                    </h5>
                                    {(!detail.flashcard_history || detail.flashcard_history.length === 0) ? (
                                        <p className="text-sm text-gray-400 italic py-10 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm text-center">Aucune flashcard révisée.</p>
                                    ) : (
                                        <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                                            {detail.flashcard_history.map((f, idx) => {
                                                const ratingStyle = f.rating === 2 
                                                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/30' 
                                                    : f.rating === 1 
                                                        ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400 border border-amber-200 dark:border-amber-800/30' 
                                                        : 'bg-rose-50 text-rose-700 dark:bg-rose-950/20 dark:text-rose-400 border border-rose-200 dark:border-rose-800/30';
                                                
                                                const ratingLabel = f.rating === 2 ? 'Facile' : f.rating === 1 ? 'Moyen' : 'Difficile';

                                                return (
                                                    <div key={f.id || idx} className="bg-white dark:bg-gray-900 p-5 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm flex flex-col gap-3">
                                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-100 dark:border-white/5 pb-2">
                                                            <div>
                                                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{f.chapter_name} &gt; {f.notion_name}</span>
                                                                <span className="text-xs text-gray-500">{new Date(f.review_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                                            </div>
                                                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold self-start sm:self-auto ${ratingStyle}`}>
                                                                SRS : {ratingLabel}
                                                            </span>
                                                        </div>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                            <div className="text-sm bg-gray-50/50 dark:bg-gray-950/20 p-3 rounded-xl border border-gray-150 dark:border-gray-800/30">
                                                                <span className="text-[10px] font-bold text-gray-400 block uppercase mb-1">Recto (Question)</span>
                                                                <p className="font-semibold text-gray-800 dark:text-gray-200">{f.flashcard_question}</p>
                                                            </div>
                                                            <div className="text-sm bg-cyan-50/20 dark:bg-cyan-950/10 p-3 rounded-xl border border-cyan-100/50 dark:border-cyan-900/10">
                                                                <span className="text-[10px] font-bold text-cyan-600 block uppercase mb-1">Verso (Réponse Attendue)</span>
                                                                <p className="font-semibold text-cyan-800 dark:text-cyan-300">{f.flashcard_answer}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB: CONFIGURATION */}
                    {activeTab === 'config' && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 gap-6">
                                <div className="bg-white dark:bg-gray-900 p-5 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm space-y-4">
                                    <h4 className="text-lg font-bold text-gray-800 dark:text-white">Attribution des tuteurs par matière</h4>
                                    <p className="text-xs text-gray-400">Associez un enseignant ou tuteur spécifique pour suivre et guider l'élève dans les matières disponibles.</p>
                                    <TuteurManager studentId={detail.id} canAssign={canAssign} />
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}

export default function AdminStudents({ isAdmin = true, userRole = 'admin' }) {
    const navigate   = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const canAssign  = isAdmin || userRole === 'supertuteur';

    const [students, setStudents]         = useState([]);
    const [loading, setLoading]           = useState(true);
    const [busy, setBusy]                 = useState(null);
    const [showCreate, setShowCreate]     = useState(false);
    const [form, setForm]                 = useState({ name: '', email: '', password: '' });
    const [formErr, setFormErr]           = useState('');
    const [creating, setCreating]         = useState(false);
    const [selectedStudent, setSelected]  = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res  = await apiClient(`${BASE}/admin_students.php?action=list`);
            const json = await res.json();
            if (json.success) setStudents(json.students);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Ouvrir automatiquement un élève via ?open=studentId
    useEffect(() => {
        const openId = parseInt(searchParams.get('open') || '0');
        if (!openId || loading) return;
        const found = students.find(s => s.id === openId);
        if (found) {
            setSelected(found);
            setSearchParams({}, { replace: true });
        } else {
            apiClient(`${BASE}/messages.php?action=student_detail&id=${openId}`)
                .then(r => r.json())
                .then(j => {
                    if (j.success) setSelected(j.student);
                    setSearchParams({}, { replace: true });
                });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, students]);

    const cycleRole = async (student) => {
        if (!isAdmin) return;
        const next = ROLES[(ROLES.indexOf(student.role) + 1) % ROLES.length];
        if (!window.confirm(`Passer ${student.name} en "${next}" ?`)) return;
        setBusy(student.id);
        await apiClient(`${BASE}/admin_students.php?action=update_role`, {
            method: 'POST',
            body: JSON.stringify({ id: student.id, role: next }),
        });
        await load();
        setBusy(null);
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setFormErr('');
        if (form.password.length < 8) { setFormErr('Le mot de passe doit contenir au moins 8 caractères.'); return; }
        setCreating(true);
        try {
            const res  = await apiClient(`${BASE}/admin_students.php?action=create_student`, {
                method: 'POST',
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (data.success) {
                setShowCreate(false);
                setForm({ name: '', email: '', password: '' });
                await load();
            } else {
                setFormErr(data.message || 'Erreur lors de la création.');
            }
        } finally { setCreating(false); }
    };

    const openMessage = (studentId) => {
        setSelected(null);
        navigate(`/messagerie?to=${studentId}`);
    };

    if (loading) return <div className="text-center py-20 text-gray-400">Chargement…</div>;

    const title = userRole === 'tuteur'
        ? `Mes utilisateurs (${students.length})`
        : `Utilisateurs (${students.length})`;

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
                {userRole !== 'tuteur' && (
                    <button onClick={() => setShowCreate(v => !v)}
                        className="px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium transition-colors">
                        + Créer un compte
                    </button>
                )}
            </div>

            {showCreate && (
                <form onSubmit={handleCreate} className="mb-6 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-white/10 p-4 flex flex-col gap-3">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Nouveau compte utilisateur</h3>
                    {formErr && <p className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 rounded px-2 py-1">{formErr}</p>}
                    <div className="flex flex-wrap gap-3">
                        <input type="text" placeholder="Prénom et nom" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required
                            className="flex-1 min-w-[180px] px-3 py-2 rounded-lg border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                        <input type="email" placeholder="Email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} required
                            className="flex-1 min-w-[180px] px-3 py-2 rounded-lg border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                        <input type="password" placeholder="Mot de passe (8 min.)" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} required
                            className="flex-1 min-w-[180px] px-3 py-2 rounded-lg border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                    </div>
                    <div className="flex gap-2 justify-end">
                        <button type="button" onClick={() => { setShowCreate(false); setFormErr(''); }}
                            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-white/10 text-gray-600 dark:text-gray-400 text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                            Annuler
                        </button>
                        <button type="submit" disabled={creating}
                            className="px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white text-sm font-medium transition-colors">
                            {creating ? 'Création…' : 'Créer'}
                        </button>
                    </div>
                </form>
            )}

            {students.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                    {userRole === 'tuteur'
                        ? 'Aucun utilisateur ne vous est encore assigné.'
                        : 'Aucun utilisateur trouvé.'}
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-white/10 overflow-hidden overflow-x-auto">
                    <table className="w-full text-sm min-w-[600px]">
                        <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 text-xs uppercase">
                            <tr>
                                <th className="p-3 text-left">Nom</th>
                                <th className="p-3 text-left">Email</th>
                                <th className="p-3 text-center">Auth</th>
                                <th className="p-3 text-center">Exercices</th>
                                <th className="p-3 text-center">Flashcards</th>
                                <th className="p-3 text-center">Quizzes</th>
                                {isAdmin && <th className="p-3 text-center">Rôle</th>}
                                <th className="p-3 text-center">Détail</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                            {students.map(s => (
                                <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                                    <td className="p-3 font-medium text-gray-800 dark:text-gray-200">{s.name}</td>
                                    <td className="p-3 text-gray-500 text-xs">{s.email}</td>
                                    <td className="p-3 text-center">
                                        <span className={`text-xs px-1.5 py-0.5 rounded ${s.has_google ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300'}`}>
                                            {s.has_google ? 'Google' : 'Email'}
                                        </span>
                                    </td>
                                    <td className="p-3 text-center text-gray-600 dark:text-gray-400">{s.skills_done}</td>
                                    <td className="p-3 text-center text-gray-600 dark:text-gray-400">{s.flashcards_count}</td>
                                    <td className="p-3 text-center text-gray-600 dark:text-gray-400">{s.quizzes_count}</td>

                                    {isAdmin && (
                                        <td className="p-3 text-center">
                                            <button
                                                onClick={() => cycleRole(s)}
                                                disabled={busy === s.id}
                                                title="Cliquer pour changer le rôle"
                                                className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors hover:opacity-75 cursor-pointer ${ROLE_STYLE[s.role] || ROLE_STYLE.etudiant}`}>
                                                {busy === s.id ? '…' : s.role}
                                            </button>
                                        </td>
                                    )}

                                    <td className="p-3 text-center">
                                        {s.role === 'etudiant' ? (
                                            <button
                                                onClick={() => setSelected(s)}
                                                className="px-2.5 py-1 rounded-lg text-xs font-medium border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                                                Voir
                                            </button>
                                        ) : (
                                            <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selectedStudent && (
                <StudentDetailPanel
                    student={selectedStudent}
                    canAssign={canAssign}
                    onClose={() => setSelected(null)}
                    onMessage={openMessage}
                />
            )}
        </div>
    );
}
